import React from 'react';

/**
 * Inline-SVG icon set for the premium trust surfaces (/service, /checkout, keyword pages).
 *
 * Why this exists: trust cards previously used raw emoji strings (⏱️🛡️✅🔧🔒🚗👁️📝✋) as the
 * `icon` prop. Emoji do not render in the SSR HTML / brand font stack, so every trust card
 * showed a □ tofu box — a "broken site" signal at every trust point. These SVGs are:
 *  - SSR-deterministic (pure markup, no font dependency) → render identically server + client
 *  - currentColor-tintable → inherit brand red/gold from the container, zero hardcoded theme
 *  - size-fixed → no layout shift (good CWV), crisp at every DPR
 *
 * Presentation-only. No API, no data, no behaviour change.
 */
const svgProps = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: 'false',
});

export const IconClock = ({ size = 28 }) => (
  <svg {...svgProps(size)}><circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 1.8" /></svg>
);
export const IconShield = ({ size = 28 }) => (
  <svg {...svgProps(size)}><path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6z" /><path d="M8.8 12l2.2 2.2 4.2-4.6" /></svg>
);
export const IconVerified = ({ size = 28 }) => (
  <svg {...svgProps(size)}><circle cx="12" cy="12" r="9" /><path d="M8.3 12.4l2.5 2.5 4.7-5.2" /></svg>
);
export const IconWrench = ({ size = 28 }) => (
  <svg {...svgProps(size)}><path d="M15 6.2a3.6 3.6 0 0 0-4.9 4.4l-5.1 5.1a1.6 1.6 0 0 0 2.3 2.3l5.1-5.1A3.6 3.6 0 0 0 16.8 8l-2.3 2.3-1.8-1.8z" /></svg>
);
export const IconLock = ({ size = 28 }) => (
  <svg {...svgProps(size)}><rect x="4.5" y="10.5" width="15" height="9.5" rx="2" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" /><path d="M12 14.5v2.5" /></svg>
);
export const IconCar = ({ size = 28 }) => (
  <svg {...svgProps(size)}><path d="M3 13.5l1.9-5.1A2.2 2.2 0 0 1 7 7h10a2.2 2.2 0 0 1 2.1 1.4L21 13.5" /><path d="M3 13.5h18V18a1 1 0 0 1-1 1h-1.6a1 1 0 0 1-1-1v-1H6.6v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /><circle cx="7.2" cy="16" r="1.1" /><circle cx="16.8" cy="16" r="1.1" /></svg>
);
export const IconEye = ({ size = 28 }) => (
  <svg {...svgProps(size)}><path d="M2.5 12S6 6 12 6s9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" /><circle cx="12" cy="12" r="2.6" /></svg>
);
export const IconDoc = ({ size = 28 }) => (
  <svg {...svgProps(size)}><path d="M7 3.5h6.5L18 8v12.5H7z" /><path d="M13.5 3.5V8H18" /><path d="M9.5 12.5h6M9.5 15.5h6M9.5 9.5h3" /></svg>
);
export const IconThumbsUp = ({ size = 28 }) => (
  <svg {...svgProps(size)}><path d="M7 11v9H4.5a.5.5 0 0 1-.5-.5v-8a.5.5 0 0 1 .5-.5z" /><path d="M7 11l3.2-7.2A2 2 0 0 1 13 5.5V9h4.6a2 2 0 0 1 2 2.4l-1.1 5.6a2 2 0 0 1-2 1.5H7" /></svg>
);
export const IconRupee = ({ size = 28 }) => (
  <svg {...svgProps(size)}><path d="M7.5 5h9M7.5 8.5h9M14.5 5c.4 4-2.4 6.2-6 6.2h-.6l6.4 7.8" /></svg>
);
export const IconStar = ({ size = 28 }) => (
  <svg {...svgProps(size)}><path d="M12 4l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.7l5.4-.8z" /></svg>
);
export const IconUsers = ({ size = 28 }) => (
  <svg {...svgProps(size)}><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 5.2a3.2 3.2 0 0 1 0 6M17.5 19a5.5 5.5 0 0 0-2.3-4.5" /></svg>
);

/** Map by semantic key — lets other surfaces (checkout trust rail) reuse by name. */
export const ICONS = {
  clock: IconClock,
  shield: IconShield,
  verified: IconVerified,
  wrench: IconWrench,
  lock: IconLock,
  car: IconCar,
  eye: IconEye,
  doc: IconDoc,
  approve: IconThumbsUp,
  rupee: IconRupee,
  star: IconStar,
  users: IconUsers,
};

export default ICONS;
